import { Injectable, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs/operators';

/**
 * Rollen, die ausschliesslich im Token stehen (nicht im NectaOne-Userprofil,
 * daher auch nicht Teil von NectaUserRole).
 */
const TOKEN_ROLE_SUPER_ADMIN = 'SuperAdmin';
const TOKEN_ROLE_NECTA_SYSTEM_ADMIN = 'necta-system-admin';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private oidc: OidcSecurityService) {}
  private accessToken$ = this.oidc.getAccessToken();


  /**
   * Alle Rollen aus dem Access Token. Bewusst als Liste: der Claim kann als
   * einzelner String, als Array oder als kommaseparierte Liste kommen, und
   * "role" und "roles" koennen beide gesetzt sein.
   */
  readonly roles = toSignal(
    this.accessToken$.pipe(
      map(token => {
        const payload = token ? this.decodeJwtPayload(token) : null;
        if (!payload) return [];
        return [
          ...this.toRoleList(payload['role']),
          ...this.toRoleList(payload['roles']),
        ];
      })
    ),
    { initialValue: [] as string[] }
  );

  readonly isSuperAdmin = computed(() => this.hasTokenRole(TOKEN_ROLE_SUPER_ADMIN));

  readonly isNectaSystemAdministrator = computed(() =>
    this.hasTokenRole(TOKEN_ROLE_NECTA_SYSTEM_ADMIN),
  );

  private hasTokenRole(role: string): boolean {
    return this.roles().includes(role);
  }

  private toRoleList(raw: unknown): string[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : String(raw).split(',');
    return list.map(role => String(role).trim()).filter(Boolean);
  }

  private decodeJwtPayload(token: string): any | null {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(parts[1].length + (4 - (parts[1].length % 4)) % 4, '=');
    try {
      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
}
